import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { fabric } from 'fabric';
import HighlighterIcon from '../../images/Icon20_Highlighter.svg';
import { connect } from 'react-redux';
import Button from '../Toolbar/Button';
import {
  setCursorTooltip,
  updateTool,
} from '../../../../redux/actions/canvas_editor';

import {
  getThicknessPx,
  hexToRgba,
  getDrawingBounds,
} from '../../helpers/fabricHelpers';

import { __ } from '../../../../core/utils';

function Highlighter({
  canvas_editor: { viewer, color, selectedThickness, fabricCanvases },
  sourceType,
  isActive,
  user_id,
  user_full_name,
  source: { width, height },
}) {
  class ClippedBrush extends fabric.PencilBrush {
    constructor(canvas, width, height) {
      super(canvas);
      this.source = { width, height };
    }

    _addPoint(point) {
      if (!point) return;

      const x = Math.max(0, Math.min(this.source.width, point.x));
      const y = Math.max(0, Math.min(this.source.height, point.y));

      super._addPoint(new fabric.Point(x, y));
      this.canvas.requestRenderAll();
    }
  }

  const handleClick = () => {
    if (!isActive) updateTool({ tool: 'highlighter' });
  };

  useEffect(() => {
    if (!isActive || !fabricCanvases) return;

    setCursorTooltip(__('canvas.tooltip.free_draw'));

    const disposers = [];

    Object.entries(fabricCanvases).forEach(([pageNumber, canvas]) => {
      canvas.selection = false;
      canvas.defaultCursor = 'crosshair';
      canvas.isDrawingMode = true;

      if (sourceType == 'pdf') {
        const originalGetPointer = canvas.getPointer.bind(canvas);
        canvas.getPointer = (e, ignoreZoom) => {
          const pointer = originalGetPointer(e, ignoreZoom);
          const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
          return {
            x: Math.max(0, Math.min(bounds.width, pointer.x)),
            y: Math.max(0, Math.min(bounds.height, pointer.y)),
          };
        };
      } else {
        canvas.freeDrawingBrush = new ClippedBrush(canvas, width, height);
      }
      const pdfScaleFactor =
        sourceType === 'pdf' ? canvas?.pdfScaleFactor || 1 : 1;

      canvas.freeDrawingBrush.color = hexToRgba(color.hex, 0.3);
      canvas.freeDrawingBrush.width =
        getThicknessPx(selectedThickness.key, 'highlighter') * pdfScaleFactor;

      const onPointerDown = () => {
        const pointerPdfScaleFactor =
          sourceType === 'pdf' ? canvas?.pdfScaleFactor || 1 : 1;
        canvas.freeDrawingBrush.width =
          getThicknessPx(selectedThickness.key, 'highlighter') *
          pointerPdfScaleFactor;
        canvas.freeDrawingBrush.color = hexToRgba(color.hex, 0.3);
        viewer?.setMouseNavEnabled(false);
      };
      const onPathCreated = (e) => {
        const obj = e.path;
        if (!obj) return;

        obj.objectType = 'freehand';
        obj.userID = user_id;
        obj.userFullName = user_full_name;
        obj.lockScalingX = true;
        obj.lockScalingY = true;
        obj.hasControls = false;
        obj.hasBorders = false;
        obj.perPixelTargetFind = true;
        obj.strokeUniform = true;
        obj.pageNumber = pageNumber;

        obj.lastGoodScaleX = obj.scaleX;
        obj.lastGoodScaleY = obj.scaleY;
        obj.lastGoodLeft = obj.left;
        obj.lastGoodTop = obj.top;

        obj.setCoords();
        viewer?.setMouseNavEnabled(true);
      };
      const onMouseUp = () => viewer?.setMouseNavEnabled(true);

      canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);
      canvas.on('path:created', onPathCreated);
      canvas.on('mouse:up', onMouseUp);

      disposers.push(() => {
        if (canvas?.upperCanvasEl)
          canvas.upperCanvasEl.removeEventListener(
            'pointerdown',
            onPointerDown
          );

        canvas.isDrawingMode = false;

        canvas.off('path:created', onPathCreated);
        canvas.off('mouse:up', onMouseUp);
      });
    });

    return () => disposers.forEach((dispose) => dispose());
  }, [
    isActive,
    user_id,
    viewer,
    color,
    selectedThickness,
    fabricCanvases,
    width,
    height,
    sourceType,
  ]);

  return (
    <Button
      id='highlighter'
      label={__('canvas.toolbar.highlighter')}
      icon={HighlighterIcon}
      active={isActive}
      extraClass={sourceType == 'pdf' && ['withSpacer']}
      onClick={handleClick}
    />
  );
}

Highlighter.propTypes = {
  isActive: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  canvas_editor: state.canvas_editor,
  user_id: state.auth.id,
  user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
});

export default connect(mapStateToProps, null)(Highlighter);
